import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, DataRow, Drawer, FormField, Radio } from "@outpave/ds";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  RadioGroup,
  Typography,
} from "@mui/material";
import MuiLink from "@mui/material/Link";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { GET_TRANSACTIONS_BY_ALLOCATION } from "../../../graphql/transaction";
import { useQuery } from "@apollo/client";
import { formatCashFull } from "../../../utils/formatCash";
import RenderMerchantDisplay from "../../common/RenderMerchantDisplay";
import CustomPanel from "components/common/CustomPanel";
import fullName from "utils/fullName";

const STATE_ROW_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "64px",
  paddingBlock: "var(--space-5)",
  borderBottom: "1px solid var(--color-border-subtle)",
};

interface SelectExpenseDrawerProps {
  open: boolean;
  handleCloseSelectExpenseDrawer: () => void;
  allocationCodeId: string;
  onSaveItems: (checkedItems: any[]) => void;
  shouldRefreshTransactions: boolean;
  setShouldRefreshTransactions: (value: boolean) => void;
}

const SelectExpenseDrawer = (props: SelectExpenseDrawerProps) => {
  const {
    open,
    handleCloseSelectExpenseDrawer,
    allocationCodeId,
    onSaveItems,
    shouldRefreshTransactions,
    setShouldRefreshTransactions,
  } = props;
  const { t } = useTranslation();

  const [checkedItems, setCheckedItems] = useState<any[]>([]);
  const [isIndividualMode, setIsIndividualMode] = useState("individual");
  const [includeReceipts, setIncludeReceipts] = useState("include");

  const {
    data: transactionsData,
    loading,
    refetch: refetchTransactions,
  } = useQuery(GET_TRANSACTIONS_BY_ALLOCATION, {
    variables: { allocationId: allocationCodeId },
    fetchPolicy: "cache-and-network",
    skip: !allocationCodeId,
  });

  const transactions = useMemo(() => {
    return (
      transactionsData?.transactions?.edges.map(({ node }: any) => ({
        id: node.id,
        merchant: node.merchant,
        description: node.description,
        amount: Math.abs(node.amount),
        currency: node.currency,
        transactionDate: node.transactionDate,
        user: {
          firstName: node.user.firstName,
          lastName: node.user.lastName,
        },
        receiptDetail: node.receiptDetail,
        merchantData: {
          url: node.merchantData?.url,
          logo: node.merchantData?.logo,
          cachedLogo: node.merchantData?.cachedLogo,
        },
      })) || []
    );
  }, [transactionsData]);

  useEffect(() => {
    if (shouldRefreshTransactions) {
      refetchTransactions();
      setShouldRefreshTransactions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefreshTransactions]);

  useEffect(() => {
    if (loading === false && transactions.length > 0) {
      setCheckedItems(transactions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const handleCheckboxChange = (transaction: any) => {
    setCheckedItems((prevCheckedItems) => {
      const isChecked = prevCheckedItems.some(
        (item) => item.id === transaction.id,
      );
      if (isChecked) {
        return prevCheckedItems.filter((item) => item.id !== transaction.id);
      } else {
        return [...prevCheckedItems, transaction];
      }
    });
  };

  const handleSelectAll = () => {
    if (checkedItems.length === transactions.length) {
      setCheckedItems([]);
    } else {
      setCheckedItems(transactions);
    }
  };

  const resetDrawer = () => {
    setCheckedItems([]);
    setIsIndividualMode("individual");
    setIncludeReceipts("include");
  };

  const handleSave = () => {
    if (isIndividualMode === "individual") {
      const individualItems = checkedItems.map((item) => ({
        id: item.id,
        productOrService: item.merchant,
        description: item.description || "",
        quantity: 1,
        unitPrice: item.amount.toFixed(2),
        receipts:
          includeReceipts === "include" && item.receiptDetail
            ? item.receiptDetail.assets.map((asset: any) => ({
                ...asset,
                receiptDetailId: item.receiptDetail.id,
              }))
            : [],
      }));
      onSaveItems(individualItems);
    } else {
      const totalAmount = checkedItems.reduce(
        (sum, item) => sum + item.amount,
        0,
      );
      const merchantNames = checkedItems
        .map((item) => item.merchant)
        .join(", ");
      const receipts =
        includeReceipts === "include"
          ? checkedItems.flatMap((item) =>
              item.receiptDetail
                ? item.receiptDetail.assets.map((asset: any) => ({
                    ...asset,
                    receiptDetailId: item.receiptDetail.id,
                  }))
                : [],
            )
          : [];
      const totalItem = {
        id: "",
        productOrService: merchantNames,
        description: "",
        quantity: 1,
        unitPrice: totalAmount.toFixed(2),
        receipts: receipts,
      };
      onSaveItems([totalItem]);
    }
    handleCloseSelectExpenseDrawer();
    resetDrawer(); // Uncheck everything
  };

  const hasTransactions = transactions.length > 0;

  return (
    <>
      <Drawer
        open={open}
        onClose={handleCloseSelectExpenseDrawer}
        title={t("Select Expense")}
        width="544px"
        loading={loading}
        footer={
          <>
            <Button
              variant="default-soft"
              onClick={handleCloseSelectExpenseDrawer}
            >
              {t("Back")}
            </Button>
            <Button
              variant="primary-filled"
              onClick={handleSave}
              disabled={checkedItems.length === 0}
            >
              {t("Import")}
            </Button>
          </>
        }
      >
        <>
          {hasTransactions && (
            <>
              <Typography variant="h6">{t("Import Options")}</Typography>
              <Stack>
                <FormField asFieldset label={t("Import Style")}>
                  <RadioGroup
                    row
                    name="mode"
                    value={isIndividualMode}
                    onChange={(e) => setIsIndividualMode(e.target.value)}
                    sx={{ paddingTop: "var(--space-3)" }}
                  >
                    <FormControlLabel
                      value="individual"
                      control={<Radio />}
                      label={
                        <Typography variant="body2">
                          {t("Separate items")}
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      value="total"
                      control={<Radio />}
                      label={
                        <Typography variant="body2">
                          {t("Combine selected expenses")}
                        </Typography>
                      }
                    />
                  </RadioGroup>
                </FormField>

                <FormField asFieldset label={t("Receipts")}>
                  <RadioGroup
                    row
                    name="receipts"
                    value={includeReceipts}
                    onChange={(e) => setIncludeReceipts(e.target.value)}
                    sx={{ paddingTop: "var(--space-3)" }}
                  >
                    <FormControlLabel
                      value="include"
                      control={<Radio />}
                      label={
                        <Typography variant="body2">
                          {t("Include receipts")}
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      value="exclude"
                      control={<Radio />}
                      label={
                        <Typography variant="body2">
                          {t("Exclude receipts")}
                        </Typography>
                      }
                    />
                  </RadioGroup>
                </FormField>
              </Stack>
            </>
          )}

          {loading ? (
            <Box sx={STATE_ROW_SX}>
              <Typography variant="body1">{t("Loading expenses")}</Typography>
            </Box>
          ) : hasTransactions ? (
            <Stack>
              <Inline wrap={false} justify="between" gap="0">
                <Typography variant="h6">{t("Transaction List")}</Typography>
                <MuiLink
                  component="button"
                  type="button"
                  onClick={handleSelectAll}
                >
                  {checkedItems.length === transactions.length
                    ? t("Deselect All")
                    : t("Select All")}
                </MuiLink>
              </Inline>

              <CustomPanel>
                {transactions.map((transaction: any, index: number) => (
                  <React.Fragment key={transaction.id}>
                    <DataRow
                      sx={{
                        paddingBlock: "var(--space-4)",
                        paddingInline: "var(--space-5)",
                        height: "auto",
                      }}
                      onClick={() => handleCheckboxChange(transaction)}
                      icon={
                        <RenderMerchantDisplay
                          merchant={transaction.merchant}
                          url={transaction.merchantData.url}
                          logo={transaction.merchantData.logo}
                          cachedLogo={transaction.merchantData.cachedLogo}
                          withTitle={false}
                          height={32}
                          width={32}
                          logoType=""
                        />
                      }
                      label={
                        <Stack gap="0" sx={{ minWidth: 0 }}>
                          <Typography variant="body1" component="span" noWrap>
                            {transaction.merchant}
                          </Typography>
                          <Typography
                            variant="caption"
                            component="span"
                            noWrap
                            sx={{ color: "var(--color-text-tertiary)" }}
                          >
                            {[
                              fullName(transaction.user),
                              new Date(
                                transaction.transactionDate,
                              ).toLocaleDateString(),
                              transaction?.receiptDetail?.assets?.length
                                ? t("receipt")
                                : t("no receipt"),
                            ].join(" \u00b7 ")}
                          </Typography>
                        </Stack>
                      }
                      secondaryTrailing={
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {formatCashFull(
                            transaction.amount,
                            transaction.currency,
                          )}
                        </Typography>
                      }
                      primaryTrailing={
                        <Checkbox
                          checked={checkedItems.some(
                            (item) => item.id === transaction.id,
                          )}
                          onChange={() => handleCheckboxChange(transaction)}
                          onClick={(event) => event.stopPropagation()}
                          aria-label={`${t("Select Expense")} ${transaction.merchant}`}
                        />
                      }
                    />
                    {index < transactions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </CustomPanel>
            </Stack>
          ) : (
            <Box sx={STATE_ROW_SX}>
              <Typography variant="body1">{t("No expenses found")}</Typography>
            </Box>
          )}
        </>
      </Drawer>
    </>
  );
};

export default SelectExpenseDrawer;
